import { SyntheticEvent, useEffect, useReducer, useRef } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import Modal from "react-modal";

import { VendorUtilities } from "@/utilities/VendorUtilities";
import { DataLoading, User } from "@/types";
import Footer from "../shared/Footer";
import ModalHeader from "../modals/ModalHeader";
import ModalBody from "../modals/ModalBody";
import ReplicatedInfo from "./ReplicatedInfo";

import Alert from "../shared/Alert";
import { Button } from "../shared/Button";
import ExternalLink from "../shared/ExternalLink";
import TextInput from "../shared/TextInput";

interface VerifySignupProps {
  accessToken?: string;
  clearServerError: () => void;
  dataLoading: DataLoading;
  displayModal: (modalName: string, display: boolean) => void;
  err: boolean;
  errMessage: string;
  handleSubmitActivationCode: (
    payload: { token: string },
    navigate: ReturnType<typeof useNavigate>
  ) => void;
  hasToken?: string;
  logOutUser: () => void;
  needsActivation: boolean;
  setLoadingToFalse: () => void;
  user: User;
}

interface VerifySignupState {
  needsActivation?: boolean;
  activationCode?: string;
  isTrialClosed?: boolean;
  resetError?: boolean;
  resetMessage?: string;
  activationCodeEmptyError?: boolean;
}

const VerifySignup = ({
  accessToken,
  clearServerError,
  dataLoading,
  displayModal,
  err,
  errMessage,
  handleSubmitActivationCode,
  hasToken,
  logOutUser,
  needsActivation,
  setLoadingToFalse,
  user
}: VerifySignupProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [state, setState] = useReducer(
    (prevState: VerifySignupState, newState: VerifySignupState) => ({
      ...prevState,
      ...newState
    }),
    {
      needsActivation: false,
      activationCode: "",
      isTrialClosed: false,
      activationCodeEmptyError: false
    }
  );
  const refActivationCode = useRef(null);

  const handleClearErrors = () => {
    if (state.resetError || state.activationCodeEmptyError) {
      setState({
        resetError: false,
        resetMessage: "",
        activationCodeEmptyError: false
      });
    }
    if (err) {
      clearServerError();
    }
  };

  const handleFormChange = (value: string) => {
    handleClearErrors();

    setState({
      activationCode: value
    });
  };

  const closeTrialExpiredModal = () => {
    setState({
      isTrialClosed: true
    });
    displayModal("trialExpired", false);
  };

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    handleClearErrors();

    const payload = {
      token: state.activationCode.trim()
    };
    handleSubmitActivationCode(payload, navigate);
  };

  useEffect(() => {
    clearServerError();
    const activationCode = location.hash.slice(1);
    if (!activationCode && refActivationCode.current) {
      refActivationCode.current.focus();
    }

    // if there is an activation token
    if (activationCode) {
      setState({
        activationCode: activationCode,
        needsActivation: true
      });
    } else {
      // if we are logged in to an existing account, log out. This is for google auth
      if (accessToken?.length > 0) {
        logOutUser();
        return;
      }
    }

    VendorUtilities.purgeVOneData();
    const { next } = params;

    // if user is logged in and already activated, redirect to the next page
    if (!!hasToken && user?.id && (!needsActivation || !state.needsActivation)) {
      navigate(`/${next || "apps"}`);
      return;
    }
    setLoadingToFalse();
  }, []);

  const userNeedsActivation = needsActivation || state.needsActivation;

  // if user is already logged in and activated
  // we return nothing while we wait for our useEffect to redirect them
  if (!!hasToken && user?.id && !userNeedsActivation) return null;

  const userAlreadyExists = errMessage === "User already exists";

  return (
      <div className="h-full w-full flex flex-col overflow-y-auto">
      <div className="flex flex-col-reverse shrink-0 min-[800px]:flex-row flex-auto w-full">
        <ReplicatedInfo needsActivation={true} />
        <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 text-left auth-pages">
          <h1 className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0 text-indigo-500 !mt-0 lg:!mt-[70px] text-2xl lg:text-3xl">
            Activate account
          </h1>
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            <p className="font-['Open_Sans',sans-serif] text-gray-410 text-base leading-[29px] font-normal mt-3">
              {!userAlreadyExists &&
                "We sent an activation code to the email address you signed up with. Copy and paste that code below to continue."}
              {userAlreadyExists &&
                "A user already exists for this email address. Please visit the log in page to log in or reset your password."}
            </p>
            {userAlreadyExists && (
              <Link
                to="/login"
                className="text-hex-2e96c2 hover:underline font-semibold mt-5 block"
              >
                Go to Log In
              </Link>
            )}
            {!userAlreadyExists && (
              <form onSubmit={handleSubmit}>
                <div className="FormSection-wrapper">
                  {err && (
                    <Alert variant="error" className="mb-1.5">
                      {errMessage}
                    </Alert>
                  )}
                  {state.activationCodeEmptyError && (
                    <Alert variant="error" className="mb-1.5">
                      Activation code cannot be blank
                    </Alert>
                  )}
                  <label htmlFor="activationCode" className="hidden">
                    Activation code
                  </label>
                  <TextInput
                    name="activationCode"
                    type="text"
                    ref={refActivationCode}
                    placeholder="Activation code"
                    value={state.activationCode}
                    onChangeValue={handleFormChange}
                  />
                </div>
                <div className="FormButton-wrapper flex flex-wrap mb-8 gap-4">
                  <Button
                    type="submit"
                    kind="primary-purple"
                    size="large"
                    className=""
                    disabled={dataLoading.activationCodeLoading}
                  >
                    {dataLoading.activationCodeLoading ? "Activating" : "Activate"}
                  </Button>
                  <div className="flex-none text-sm items-center justify-center flex flex-col relative">
                    <p className="text-gray-410">
                      Didn't get a code?{" "}
                      <Link
                        to="/signup"
                        className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                      >
                        Request another
                      </Link>
                    </p>
                  </div>
                </div>
              </form>
            )}
          </div>
          <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
            <Footer />
          </div>
        </div>
      </div>
      <Modal
        isOpen={!state.isTrialClosed && errMessage === "Trial expired"}
        contentLabel="Trial Expired Modal"
        ariaHideApp={false}
        className="auth-pages Modal DefaultSize"

        onRequestClose={closeTrialExpiredModal}
      >
        <ModalHeader title="Trial Expired" requestClose={closeTrialExpiredModal} />
        <ModalBody>
          <div>
            Your team's free trial has ended. Please contact us at{" "}
            <ExternalLink
              href="mailto:contact@replicated.com"
              className="text-hex-337ab7 font-medium hover:underline hover:cursor-pointer"
            >
              contact@replicated.com{" "}
            </ExternalLink>
            if you need an extension.
            <div className="FormButton-wrapper text-right">
              <Button
                type="button"
                kind="secondary-gray"
                className="mr-3 mt-3"
                onClick={closeTrialExpiredModal}
              >
                Cancel
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default VerifySignup;
